/* eslint-disable max-lines */
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import ReactDatetime from 'react-datetime';
import { Button, Col, Container, Input, InputGroup, Row, Spinner, Table } from 'reactstrap';
import moment from 'moment';
import CommonHeader from '../../../components/Headers/CommonHeader';
import { Context as AuthContext } from '../../../contexts/AuthContext';
import DailyScheduleService from '../../../services/DailySchedule';
import {
  DailyScheduleAnnouncementDTO,
  DailyScheduleDTO,
  DailyScheduleEmployeeDTO,
  DailyScheduleEntryDTO,
  DailyScheduleFloaterBlockDTO,
  DailyScheduleFloaterDTO,
  DailyScheduleRoomDTO,
  HighlightColor,
} from '../../../interfaces/DailyScheduleInterface';
import DashboardSummaryBar from './components/DashboardSummaryBar';
import AnnouncementsPanel from './components/AnnouncementsPanel';
import ValidationPanel from './components/ValidationPanel';
import RoomSection from './components/RoomSection';
import FloaterSection from './components/FloaterSection';
import './daily-schedule.scss';

function navigateWeekday(date: string, direction: -1 | 1): string {
  let d = moment(date, 'MM/DD/YYYY').add(direction, 'days');
  while (d.day() === 0 || d.day() === 6) {
    d = d.add(direction, 'days');
  }
  return d.format('MM/DD/YYYY');
}

const DailySchedulePage = () => {
  const { token, currentSchool, user } = useContext(AuthContext) as any;
  const location = useLocation();

  const schoolId = currentSchool?.value || user?.employee?.schoolId;

  const getInitialDate = (): string => {
    const params = new URLSearchParams(location.search);
    const dateParam = params.get('date');
    if (dateParam && moment(dateParam, 'YYYY-MM-DD', true).isValid()) {
      return moment(dateParam, 'YYYY-MM-DD').format('MM/DD/YYYY');
    }
    return moment().format('MM/DD/YYYY');
  };

  const [selectedDate, setSelectedDate] = useState<string>(getInitialDate);
  const [schedule, setSchedule] = useState<DailyScheduleDTO | null>(null);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [employees, setEmployees] = useState<DailyScheduleEmployeeDTO[]>([]);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [filterText, setFilterText] = useState('');

  const saveTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const loadEmployees = useCallback(async () => {
    if (!token || !schoolId) return;
    try {
      const result = await DailyScheduleService(token).listEmployees(schoolId);
      setEmployees(result?.data || []);
    } catch {
      /* silent */
    }
  }, [token, schoolId]);

  const loadSchedule = useCallback(
    async (date: string) => {
      if (!token || !schoolId) return;
      setLoading(true);
      setValidationErrors([]);
      try {
        const isoDate = moment(date, 'MM/DD/YYYY').format('YYYY-MM-DD');
        try {
          const result = await DailyScheduleService(token).getByDate(schoolId, isoDate);
          setSchedule(result?.data || null);
        } catch (e: any) {
          if (e?.response?.status === 404) {
            setSchedule(null);
          } else {
            toast.error(e?.response?.data?.message || 'Failed to load schedule.');
          }
        }
      } finally {
        setLoading(false);
      }
    },
    [token, schoolId],
  );

  useEffect(() => {
    loadEmployees();
  }, [loadEmployees]);

  useEffect(() => {
    loadSchedule(selectedDate);
  }, [loadSchedule, selectedDate]);

  const handleCreateSchedule = async () => {
    if (!token || !schoolId) return;
    setLoading(true);
    try {
      const isoDate = moment(selectedDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const result = await DailyScheduleService(token).create(schoolId, { date: isoDate });
      setSchedule(result?.data || result);
      toast.success('Schedule created!');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to create schedule.');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!schedule) return;
    setSending(true);
    setValidationErrors([]);
    try {
      await DailyScheduleService(token).send(schedule.id);
      setSchedule((prev) => (prev ? { ...prev, status: 'SENT' } : prev));
      toast.success('Schedule sent successfully!');
    } catch (e: any) {
      const errors = e?.response?.data?.errors;
      if (errors && typeof errors === 'object' && errors.errors) {
        setValidationErrors(errors.errors);
        toast.error('Please fix the schedule issues before sending.');
      } else {
        toast.error(e?.response?.data?.message || 'Failed to send schedule.');
      }
    } finally {
      setSending(false);
    }
  };

  const handleCopyPrevious = async () => {
    if (!token || !schoolId) return;
    try {
      const isoDate = moment(selectedDate, 'MM/DD/YYYY').format('YYYY-MM-DD');
      const result = await DailyScheduleService(token).copyPrevious(schoolId, { targetDate: isoDate });
      setSchedule(result?.data || result);
      toast.success('Previous schedule copied!');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'No previous schedule found.');
    }
  };

  const handleClearSchedule = async () => {
    if (!schedule) return;
    if (!window.confirm('Clear all rooms and entries for this day?')) return;
    try {
      await DailyScheduleService(token).clear(schedule.id);
      setSchedule((prev) => (prev ? { ...prev, rooms: [], announcements: [], status: 'DRAFT' } : prev));
      toast.success('Schedule cleared.');
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to clear schedule.');
    }
  };

  const handleAddRoom = async () => {
    if (!schedule) return;
    try {
      const result = await DailyScheduleService(token).addRoom(schedule.id, {
        roomName: 'New Room',
        sortOrder: (schedule.rooms || []).length,
      });
      const newRoom: DailyScheduleRoomDTO = { ...(result?.data || result), entries: [] };
      setSchedule((prev) => (prev ? { ...prev, rooms: [...(prev.rooms || []), newRoom] } : prev));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add room.');
    }
  };

  const handleRoomNameChange = (roomId: string, name: string) => {
    setSchedule((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rooms: prev.rooms.map((r) => (r.id === roomId ? { ...r, roomName: name } : r)),
      };
    });

    clearTimeout(saveTimersRef.current[`room-name-${roomId}`]);
    saveTimersRef.current[`room-name-${roomId}`] = setTimeout(async () => {
      if (!schedule) return;
      try {
        await DailyScheduleService(token).updateRoom(schedule.id, roomId, { roomName: name });
      } catch {
        toast.error('Failed to save room name.');
      }
    }, 400);
  };

  const handleRoomColorChange = async (roomId: string, color: HighlightColor) => {
    setSchedule((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rooms: prev.rooms.map((r) => (r.id === roomId ? { ...r, highlightColor: color } : r)),
      };
    });
    if (!schedule) return;
    try {
      await DailyScheduleService(token).updateRoom(schedule.id, roomId, { highlightColor: color });
    } catch {
      toast.error('Failed to save room color.');
    }
  };

  const handleRemoveRoom = async (roomId: string) => {
    if (!schedule) return;
    if (!window.confirm('Remove this room and all its staff entries?')) return;
    try {
      await DailyScheduleService(token).removeRoom(schedule.id, roomId);
      setSchedule((prev) => (prev ? { ...prev, rooms: (prev.rooms || []).filter((r) => r.id !== roomId) } : prev));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to remove room.');
    }
  };

  const handleAddEntry = async (roomId: string) => {
    if (!schedule) return;
    try {
      const room = (schedule.rooms || []).find((r) => r.id === roomId);
      const result = await DailyScheduleService(token).addEntry(schedule.id, roomId, {
        staffStatus: 'ON_SCHEDULE',
        sortOrder: (room?.entries || []).length,
      });
      const newEntry: DailyScheduleEntryDTO = result?.data || result;
      setSchedule((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rooms: prev.rooms.map((r) => (r.id === roomId ? { ...r, entries: [...(r.entries || []), newEntry] } : r)),
        };
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add staff.');
    }
  };

  const handleEntryChange = (entryId: string, field: string, value: any) => {
    if (entryId.startsWith('__save__')) {
      const realId = entryId.replace('__save__', '');
      if (!schedule) return;
      DailyScheduleService(token)
        .updateEntry(schedule.id, realId, { [field]: value })
        .catch(() => toast.error('Failed to save entry.'));
      return;
    }

    setSchedule((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        rooms: prev.rooms.map((r) => ({
          ...r,
          entries: (r.entries || []).map((e) => (e.id === entryId ? { ...e, [field]: value } : e)),
        })),
      };
    });
  };

  const handleRemoveEntry = async (entryId: string) => {
    if (!schedule) return;
    try {
      await DailyScheduleService(token).removeEntry(schedule.id, entryId);
      setSchedule((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          rooms: prev.rooms.map((r) => ({
            ...r,
            entries: (r.entries || []).filter((e) => e.id !== entryId),
          })),
        };
      });
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to remove staff.');
    }
  };

  const handleAddAnnouncement = async (text: string) => {
    if (!schedule) return;
    try {
      const result = await DailyScheduleService(token).addAnnouncement(schedule.id, { text });
      const newAnn: DailyScheduleAnnouncementDTO = result?.data || result;
      setSchedule((prev) => (prev ? { ...prev, announcements: [...(prev.announcements || []), newAnn] } : prev));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add announcement.');
    }
  };

  const handleRemoveAnnouncement = async (id: string) => {
    if (!schedule) return;
    try {
      await DailyScheduleService(token).removeAnnouncement(schedule.id, id);
      setSchedule((prev) =>
        prev ? { ...prev, announcements: (prev.announcements || []).filter((a) => a.id !== id) } : prev,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to remove announcement.');
    }
  };

  const handleAddFloater = async (employeeId: string | null) => {
    if (!schedule) return;
    try {
      const result = await DailyScheduleService(token).addFloater(schedule.id, {
        employeeId,
        sortOrder: (schedule.floaters || []).length,
      });
      const newFloater: DailyScheduleFloaterDTO = result?.data || result;
      setSchedule((prev) => (prev ? { ...prev, floaters: [...(prev.floaters || []), newFloater] } : prev));
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add floater.');
    }
  };

  const handleUpdateFloater = async (floaterId: string, employeeId: string | null) => {
    if (!schedule) return;
    try {
      const result = await DailyScheduleService(token).updateFloater(schedule.id, floaterId, { employeeId });
      const updated: DailyScheduleFloaterDTO = result?.data || result;
      setSchedule((prev) =>
        prev ? { ...prev, floaters: (prev.floaters || []).map((f) => (f.id === floaterId ? updated : f)) } : prev,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to update floater.');
    }
  };

  const handleRemoveFloater = async (floaterId: string) => {
    if (!schedule) return;
    try {
      await DailyScheduleService(token).removeFloater(schedule.id, floaterId);
      setSchedule((prev) =>
        prev ? { ...prev, floaters: (prev.floaters || []).filter((f) => f.id !== floaterId) } : prev,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to remove floater.');
    }
  };

  const handleAddFloaterBlock = async (floaterId: string, data: Partial<DailyScheduleFloaterBlockDTO>) => {
    if (!schedule) return;
    try {
      const result = await DailyScheduleService(token).addFloaterBlock(schedule.id, floaterId, data);
      const newBlock: DailyScheduleFloaterBlockDTO = result?.data || result;
      setSchedule((prev) =>
        prev
          ? {
              ...prev,
              floaters: (prev.floaters || []).map((f) =>
                f.id === floaterId ? { ...f, blocks: [...(f.blocks || []), newBlock] } : f,
              ),
            }
          : prev,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to add block.');
    }
  };

  const handleUpdateFloaterBlock = async (
    floaterId: string,
    blockId: string,
    data: Partial<DailyScheduleFloaterBlockDTO>,
  ) => {
    if (!schedule) return;
    try {
      const result = await DailyScheduleService(token).updateFloaterBlock(schedule.id, floaterId, blockId, data);
      const updated: DailyScheduleFloaterBlockDTO = result?.data || result;
      setSchedule((prev) =>
        prev
          ? {
              ...prev,
              floaters: (prev.floaters || []).map((f) =>
                f.id === floaterId
                  ? { ...f, blocks: (f.blocks || []).map((b) => (b.id === blockId ? updated : b)) }
                  : f,
              ),
            }
          : prev,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to update block.');
    }
  };

  const handleRemoveFloaterBlock = async (floaterId: string, blockId: string) => {
    if (!schedule) return;
    try {
      await DailyScheduleService(token).removeFloaterBlock(schedule.id, floaterId, blockId);
      setSchedule((prev) =>
        prev
          ? {
              ...prev,
              floaters: (prev.floaters || []).map((f) =>
                f.id === floaterId ? { ...f, blocks: (f.blocks || []).filter((b) => b.id !== blockId) } : f,
              ),
            }
          : prev,
      );
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Failed to remove block.');
    }
  };

  const filteredRooms = (schedule?.rooms || []).filter((room) => {
    if (!filterText) return true;
    const f = filterText.toLowerCase();
    const roomMatch = room.roomName.toLowerCase().includes(f);
    const entryMatch = (room.entries || []).some((e) => {
      const name = e.employee ? `${e.employee.user.firstName} ${e.employee.user.lastName}`.toLowerCase() : '';
      return name.includes(f);
    });
    return roomMatch || entryMatch;
  });

  const isSent = schedule?.status === 'SENT';

  return (
    <>
      <CommonHeader />
      <Container fluid className="daily-schedule-page mt--7">
        <div className="d-flex align-items-center justify-content-between flex-wrap mb-3" style={{ gap: '0.75rem' }}>
          <div style={{ zIndex: 1 }}>
            <h2 className="page-title mb-0">Schedule Management</h2>
            <p className="page-subtitle mb-0">Create and send daily and weekly break schedules</p>
            <div
              className="d-inline-flex align-items-center p-1 mt-2"
              role="tablist"
              style={{ backgroundColor: '#e8ecf1', borderRadius: 999, gap: 0 }}
            >
              <span
                role="tab"
                aria-selected
                style={{
                  borderRadius: 999,
                  padding: '0.45rem 1.15rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.35,
                  fontWeight: 500,
                  boxShadow: '0 1px 4px rgba(94, 114, 228, 0.45)',
                  transition: 'background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
                }}
                className="bg-primary text-white border-0"
              >
                Daily Schedule
              </span>
              <Link
                to="/schedule/weekly"
                role="tab"
                aria-selected={false}
                style={{
                  borderRadius: 999,
                  padding: '0.45rem 1.15rem',
                  fontSize: '0.875rem',
                  lineHeight: 1.35,
                  fontWeight: 500,
                  backgroundColor: 'transparent',
                  boxShadow: 'none',
                  transition: 'background-color 0.15s ease, box-shadow 0.15s ease, color 0.15s ease',
                  textDecoration: 'none',
                }}
                className="text-dark border-0"
              >
                Weekly Schedule
              </Link>
            </div>
          </div>
          <div className="d-flex align-items-center flex-wrap" style={{ gap: '0.5rem' }}>
            <Button
              color="secondary"
              outline
              size="sm"
              className="schedule-header-btn"
              onClick={() => setSelectedDate(navigateWeekday(selectedDate, -1))}
            >
              ‹
            </Button>
            <div className="date-picker-wrapper">
              <ReactDatetime
                value={selectedDate}
                onChange={(v) => {
                  const formatted = moment.isMoment(v) ? v.format('MM/DD/YYYY') : String(v);
                  setSelectedDate(formatted);
                }}
                timeFormat={false}
                dateFormat="MM/DD/YYYY"
                closeOnSelect
                inputProps={{ placeholder: 'Select date', style: { minWidth: 130 } }}
              />
            </div>
            <Button
              color="secondary"
              outline
              size="sm"
              className="schedule-header-btn"
              onClick={() => setSelectedDate(navigateWeekday(selectedDate, 1))}
            >
              ›
            </Button>
            {schedule && (
              <Button
                color={isSent ? 'success' : 'primary'}
                disabled={sending || isSent}
                onClick={handleSend}
                size="sm"
              >
                {sending ? <Spinner size="sm" /> : isSent ? '✓ Sent' : 'Send Schedule'}
              </Button>
            )}
          </div>
        </div>

        {!schoolId && (
          <div className="section-card text-center py-4">
            <p className="text-muted mb-0">Please select a school to view the daily schedule.</p>
          </div>
        )}

        {loading && !!schoolId && (
          <div className="text-center py-4">
            <Spinner color="primary" />
          </div>
        )}

        {!loading && !!schoolId && !schedule && (
          <div className="section-card text-center py-4">
            <p className="text-muted mb-3">No schedule found for {selectedDate}.</p>
            <Button color="primary" onClick={handleCreateSchedule}>
              + Create Schedule for This Day
            </Button>
            <Button color="secondary" outline className="ml-2" onClick={handleCopyPrevious}>
              Copy Previous Day
            </Button>
          </div>
        )}

        {!loading && !!schoolId && schedule && (
          <>
            <DashboardSummaryBar schedule={schedule} />

            <div className="section-card mb-3">
              <Row className="align-items-center" style={{ gap: '0.5rem' }}>
                <Col xs="auto">
                  <span className="section-title mb-0" style={{ display: 'inline-block' }}>
                    Quick Actions
                  </span>
                </Col>
                <Col xs="auto">
                  <Button color="info" outline size="sm" onClick={handleCopyPrevious}>
                    📋 Copy Previous Day
                  </Button>
                </Col>
                <Col xs="auto">
                  <Button color="danger" outline size="sm" onClick={handleClearSchedule}>
                    🗑 Clear Schedule
                  </Button>
                </Col>
                <Col>
                  <InputGroup size="sm" style={{ maxWidth: 260 }}>
                    <Input
                      placeholder="Filter by name or room..."
                      value={filterText}
                      onChange={(e) => setFilterText(e.target.value)}
                    />
                    {filterText && (
                      <Button color="secondary" outline onClick={() => setFilterText('')}>
                        ×
                      </Button>
                    )}
                  </InputGroup>
                </Col>
              </Row>
            </div>

            <AnnouncementsPanel
              announcements={schedule.announcements || []}
              onAdd={handleAddAnnouncement}
              onRemove={handleRemoveAnnouncement}
            />

            <ValidationPanel errors={validationErrors} />

            <div className="section-card mb-3 p-0 schedule-table-wrapper">
              <Table borderless responsive className="mb-0">
                <thead>
                  <tr>
                    <th>Room</th>
                    <th>Staff Member</th>
                    <th>Schedule (Shift / Break)</th>
                    <th>Status</th>
                    <th>Notes</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {filteredRooms.map((room) => (
                    <RoomSection
                      key={room.id}
                      room={room}
                      employees={employees}
                      onRoomNameChange={handleRoomNameChange}
                      onRoomColorChange={handleRoomColorChange}
                      onRemoveRoom={handleRemoveRoom}
                      onAddEntry={handleAddEntry}
                      onEntryChange={handleEntryChange}
                      onRemoveEntry={handleRemoveEntry}
                    />
                  ))}
                  {filteredRooms.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center text-muted py-4">
                        {filterText ? 'No results match your filter.' : 'No rooms added yet.'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
              <div className="p-3">
                <Button color="primary" outline size="sm" onClick={handleAddRoom}>
                  + Add Room
                </Button>
              </div>
            </div>

            <FloaterSection
              floaters={schedule.floaters || []}
              employees={employees}
              scheduleId={schedule.id}
              onAddFloater={handleAddFloater}
              onUpdateFloater={handleUpdateFloater}
              onRemoveFloater={handleRemoveFloater}
              onAddBlock={handleAddFloaterBlock}
              onUpdateBlock={handleUpdateFloaterBlock}
              onRemoveBlock={handleRemoveFloaterBlock}
            />
          </>
        )}
      </Container>
    </>
  );
};

export default DailySchedulePage;
